import {
  isCapacityIncomplete,
  type TrainingGpuCapacity,
  type TrainingGpuClusterUsage,
  type TrainingGpuWorkspaceUsage,
} from "@/api/trainingGpu";
import type { StatTileProps } from "@/components/metric";
import type { UsageTrendPoint } from "./charts";
import { formatGpuHours, formatUsd } from "./gpuUsageFormat";

/**
 * GPU 사용량 요약 띠의 순수 모델. 세 응답을 타일 문구와 차트 점으로 바꾼다.
 *
 * 세 응답은 출처가 다르다. 그래서 숫자마다 출처를 하나로 고정한다.
 *
 * - workspace 사용량(`usage`): attempt 실행 구간으로 센 값이다. 기간 GPU-hours, 계층 비중,
 *   사용분 비용이 여기서 온다. 쿼터 표의 값과 같은 출처다.
 * - 클러스터 점유(`cluster`): 5분 표본을 적분한 값이다. 가동률, 유휴, 보유 비용, 차트가 여기서
 *   온다. 이 출처의 "사용"은 workspace 사용량의 합보다 크다. 그래서 타일에 이 출처의 사용 합계를
 *   숫자로 싣지 않는다. 같은 화면에 "사용량"이 두 개 있으면 안 된다.
 * - 가용량(`capacity`): 지금 시점의 스냅샷이다.
 *
 * 응답이 없는 자리(조회 전·실패)는 0 이 아니라 `—` 로 표시한다.
 */

const EMPTY_VALUE = "—";

export type GpuUsageStripTile = StatTileProps & { key: string };

export type GpuUsageStripSources = {
  usage: TrainingGpuWorkspaceUsage | null;
  cluster: TrainingGpuClusterUsage | null;
  capacity: TrainingGpuCapacity | null;
};

export function buildGpuUsageStripTiles(sources: GpuUsageStripSources): GpuUsageStripTile[] {
  return [
    usageTile(sources.usage),
    utilizationTile(sources.cluster),
    idleTile(sources.cluster),
    costTile(sources.cluster, sources.usage),
    occupancyTile(sources.capacity),
  ];
}

function usageTile(usage: TrainingGpuWorkspaceUsage | null): GpuUsageStripTile {
  return {
    key: "usage",
    label: "기간 GPU-hours",
    value: usage === null ? EMPTY_VALUE : formatGpuHours(usage.total_gpu_hours),
    hint: usage === null ? undefined : tierShareHint(usage),
  };
}

// 계층별 비중. 예) "ondemand 60% · flex 40%". 계층 이름은 서버 값을 그대로 쓴다. 닫힌 어휘가
// 아니기 때문이다. 사용이 없으면 비중이 없다.
function tierShareHint(usage: TrainingGpuWorkspaceUsage): string | undefined {
  if (usage.total_gpu_hours <= 0 || usage.by_tier.length === 0) {
    return undefined;
  }
  return usage.by_tier
    .map((share) => `${share.key} ${formatPercent(share.gpu_hours / usage.total_gpu_hours)}`)
    .join(" · ");
}

function utilizationTile(cluster: TrainingGpuClusterUsage | null): GpuUsageStripTile {
  // 보유가 0 이면 가동률이 없다(null). 0% 와 다르다.
  const utilization = cluster?.utilization ?? null;
  return {
    key: "utilization",
    label: "가동률",
    value: utilization === null ? EMPTY_VALUE : formatPercent(utilization),
    ratio: utilization ?? undefined,
    hint: cluster === null ? undefined : `보유 ${formatGpuHours(cluster.held_gpu_hours)} GPU-hours`,
  };
}

function idleTile(cluster: TrainingGpuClusterUsage | null): GpuUsageStripTile {
  return {
    key: "idle",
    label: "유휴 GPU-hours",
    value: cluster === null ? EMPTY_VALUE : formatGpuHours(cluster.idle_gpu_hours),
    hint:
      cluster === null || cluster.days.length === 0
        ? undefined
        : `하루 평균 ${formatGpuHours(cluster.idle_gpu_hours / cluster.days.length)}`,
  };
}

// 비용은 보유 기준이 주 값이다. 클러스터가 쓴 돈은 사용 여부와 무관하게 보유한 GPU 에서 나간다.
// 사용분은 그중 실제 작업에 쓴 몫이다. 서버에 단가가 없으면 둘 다 값이 없다.
function costTile(
  cluster: TrainingGpuClusterUsage | null,
  usage: TrainingGpuWorkspaceUsage | null,
): GpuUsageStripTile {
  const usedCost = usage?.total_cost ?? null;
  return {
    key: "cost",
    label: "비용",
    value: cluster === null ? EMPTY_VALUE : formatUsd(cluster.cost),
    hint: usedCost === null ? "보유 기준" : `보유 기준 · 사용분 ${formatUsd(usedCost)}`,
  };
}

function occupancyTile(capacity: TrainingGpuCapacity | null): GpuUsageStripTile {
  // 상류가 읽지 못한 값은 0 으로 온다. 그래서 불완전한 응답은 읽지 못한 것으로 판정한다.
  if (capacity === null || isCapacityIncomplete(capacity)) {
    return { key: "occupancy", label: "지금 점유", value: EMPTY_VALUE };
  }
  return {
    key: "occupancy",
    label: "지금 점유",
    value: `${capacity.workload_running_gpus} / ${capacity.total_allocatable_gpus} GPU`,
    hint: `여유 ${capacity.free_gpus}`,
  };
}

function formatPercent(ratio: number): string {
  return `${Math.round(ratio * 100)}%`;
}

// 기간의 모든 UTC 일을 점으로 만든다. 표본이 없는 날은 값이 null 이다. 차트는 그 자리에서 선을
// 끊는다. x 축 문구는 UTC 일의 `MM-DD` 다. 서버가 준 날짜 문자열에서 그대로 자른다. 시각으로
// 읽어 로컬 날짜로 바꾸면 보는 사람의 시간대에 따라 하루가 밀린다.
export function toUsageTrendPoints(cluster: TrainingGpuClusterUsage): UsageTrendPoint[] {
  const dayByPeriod = new Map(cluster.days.map((day) => [day.period, day]));
  return cluster.periods.map((period) => {
    const day = dayByPeriod.get(period);
    return {
      key: period,
      label: period.slice(5),
      held: day?.held_gpu_hours ?? null,
      used: day?.used_gpu_hours ?? null,
    };
  });
}

// 차트 전체의 접근성 이름. 기간과 합계를 한 문장으로 말한다.
export function describeUsageTrend(cluster: TrainingGpuClusterUsage): string {
  const first = cluster.periods[0];
  const last = cluster.periods[cluster.periods.length - 1];
  if (first === undefined || last === undefined) {
    return "일별 GPU-hours 추세";
  }
  return `${first}부터 ${last}까지 일별 GPU-hours. 보유 ${formatGpuHours(cluster.held_gpu_hours)}, 사용 ${formatGpuHours(cluster.used_gpu_hours)}.`;
}
